/**
 * Юниты и конфигурация осей.
 *
 * CLAUDE.md: порог износа зависит от ПОЗИЦИИ колеса, а не только от типа узла.
 * На рулевой оси минимум протектора 4/32", на остальных 2/32".
 * Поэтому шаблон осмотра генерируется под конфигурацию конкретного юнита,
 * а не берётся плоским списком.
 */
import { z } from 'zod';
import { objectIdSchema } from './common.js';
export const UNIT_KINDS = ['tractor', 'trailer'];
export const AXLE_ROLES = ['steer', 'drive', 'trailer', 'lift'];
/** Типы тормозных камер: от типа зависит предел хода штока (49 CFR 393.47). */
export const BRAKE_CHAMBER_TYPES = [
    'type_16',
    'type_20',
    'type_24',
    'type_24_long',
    'type_30',
    'type_30_long',
];
export const axleSchema = z.object({
    /** Порядковый номер оси внутри своей роли, начиная с 1. */
    index: z.number().int().min(1).max(6),
    role: z.enum(AXLE_ROLES),
    /** 1 — одинарные колёса, 2 — сдвоенные (внешнее + внутреннее). */
    wheelsPerSide: z.union([z.literal(1), z.literal(2)]),
    chamberType: z.enum(BRAKE_CHAMBER_TYPES).nullable(),
});
export const axleConfigSchema = z.object({
    axles: z.array(axleSchema).min(1).max(8),
});
export const SIDES = ['left', 'right'];
const ROLE_LABEL = {
    steer: 'Steer',
    drive: 'Drive',
    trailer: 'Trailer',
    lift: 'Lift',
};
const SIDE_LABEL = { left: 'left', right: 'right' };
/**
 * Разворачивает конфигурацию осей в список позиций колёс.
 * Чистая функция в contracts: фронт и бэк обязаны получать один и тот же
 * список, иначе замеры не сойдутся с шаблоном.
 */
export function wheelPositions(config) {
    const positions = [];
    for (const axle of config.axles) {
        for (const side of SIDES) {
            const duals = axle.wheelsPerSide === 2 ? ['outer', 'inner'] : [null];
            for (const dual of duals) {
                const id = [axle.role, axle.index, side, dual].filter((x) => x !== null).join('_');
                const dualLabel = dual === 'outer' ? ' outer' : dual === 'inner' ? ' inner' : '';
                positions.push({
                    id,
                    axleIndex: axle.index,
                    role: axle.role,
                    side,
                    dual,
                    chamberType: axle.chamberType,
                    label: `${ROLE_LABEL[axle.role]} ${axle.index}, ${SIDE_LABEL[side]}${dualLabel}`,
                });
            }
        }
    }
    return positions;
}
export function brakePositions(config) {
    const positions = [];
    for (const axle of config.axles) {
        if (axle.chamberType === null)
            continue;
        for (const side of SIDES) {
            positions.push({
                id: `brake_${axle.role}_${axle.index}_${side}`,
                axleIndex: axle.index,
                role: axle.role,
                side,
                chamberType: axle.chamberType,
                label: `${ROLE_LABEL[axle.role]} ${axle.index} brake, ${SIDE_LABEL[side]}`,
            });
        }
    }
    return positions;
}
/** Типовые конфигурации — чтобы завести флот не вручную по каждой оси. */
export const AXLE_PRESETS = {
    tractor_6x4: {
        label: 'Tractor 6x4 (10 wheels)',
        kind: 'tractor',
        config: {
            axles: [
                { index: 1, role: 'steer', wheelsPerSide: 1, chamberType: 'type_20' },
                { index: 1, role: 'drive', wheelsPerSide: 2, chamberType: 'type_30_long' },
                { index: 2, role: 'drive', wheelsPerSide: 2, chamberType: 'type_30_long' },
            ],
        },
    },
    tractor_4x2: {
        label: 'Tractor 4x2 (6 wheels)',
        kind: 'tractor',
        config: {
            axles: [
                { index: 1, role: 'steer', wheelsPerSide: 1, chamberType: 'type_20' },
                { index: 1, role: 'drive', wheelsPerSide: 2, chamberType: 'type_30' },
            ],
        },
    },
    trailer_tandem: {
        label: 'Semi-trailer, tandem (8 wheels)',
        kind: 'trailer',
        config: {
            axles: [
                { index: 1, role: 'trailer', wheelsPerSide: 2, chamberType: 'type_30_long' },
                { index: 2, role: 'trailer', wheelsPerSide: 2, chamberType: 'type_30_long' },
            ],
        },
    },
};
/* ------------------------------- схемы API ------------------------------- */
export const createUnitSchema = z.object({
    kind: z.enum(UNIT_KINDS),
    unitNumber: z.string().min(1).max(32).regex(/^[A-Z0-9_-]+$/i),
    vin: z.string().length(17).regex(/^[A-HJ-NPR-Z0-9]+$/i).nullable().default(null),
    make: z.string().max(60).nullable().default(null),
    model: z.string().max(60).nullable().default(null),
    year: z.number().int().min(1980).max(2100).nullable().default(null),
    siteId: objectIdSchema.nullable().default(null),
    axleConfig: axleConfigSchema,
});
export const updateUnitSchema = createUnitSchema.partial().omit({ unitNumber: true });
export const UNIT_STATUSES = ['active', 'out_of_service', 'retired'];
export const unitSchema = z.object({
    id: objectIdSchema,
    orgId: objectIdSchema,
    siteId: objectIdSchema.nullable(),
    kind: z.enum(UNIT_KINDS),
    unitNumber: z.string(),
    vin: z.string().nullable(),
    make: z.string().nullable(),
    model: z.string().nullable(),
    year: z.number().int().nullable(),
    status: z.enum(UNIT_STATUSES),
    axleConfig: axleConfigSchema,
    createdAt: z.coerce.date(),
});
/**
 * Назначение водителя на юнит.
 * Допущение A5: Driver Score не делаем, но фиксировать назначенного водителя
 * обязательно — метрику можно посчитать задним числом, назначения нельзя.
 */
export const createAssignmentSchema = z.object({
    unitId: objectIdSchema,
    driverUserId: objectIdSchema,
    from: z.coerce.date(),
    to: z.coerce.date().nullable().default(null),
});
export const qrTokenSchema = z.string().min(16).max(64).regex(/^[A-Za-z0-9_-]+$/);
/* --------------------------- юридическая блокировка ------------------------ */
/**
 * Legal hold: пока он активен, ретеншен-политика юнит не трогает,
 * а его вложения дополнительно защищены WORM-блокировкой в хранилище.
 *
 * Снятие — отдельное право: блокировку накладывают, когда началось
 * разбирательство, и снимать её должен не тот, кого разбирательство касается.
 */
export const placeLegalHoldSchema = z.object({
    reason: z.string().min(10).max(1000),
    /** Ссылка на дело, полис или требование — чтобы блокировка не была безымянной. */
    caseReference: z.string().max(200).nullable().default(null),
});
export const releaseLegalHoldSchema = z.object({
    reason: z.string().min(10).max(1000),
});
export const legalHoldSchema = z.object({
    active: z.boolean(),
    reason: z.string().nullable(),
    caseReference: z.string().nullable(),
    placedByUserId: objectIdSchema.nullable(),
    placedAt: z.coerce.date().nullable(),
    releasedByUserId: objectIdSchema.nullable(),
    releasedAt: z.coerce.date().nullable(),
    /** Сколько объектов реально заблокировано в хранилище. */
    heldObjectCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=unit.js.map